import { makeObservable, observable, action } from 'mobx';
import axios, { CancelToken } from 'axios';
import CourseStore from './CourseStore';
import UserStore from './UserStore';
import Api from '../api/CMDBApi';
import LanguageStore from './LanguageStore';
import _ from 'lodash';

class SearchStore {
  userResults = null;
  resourceResults = null;
  locationResults = [];
  totalResourceResultCount = 0;
  totalUserResultCount = 0;
  totalLocationResultCount = 0;
  isSearching = false;
  awrCancelTokenSource = null;
  userCancelTokenSource = null;
  locationCancelTokenSource = null;
  awrSearchResults = [];
  totalAwrSearchResultCount = 0;
  learningsSearchResults = [];
  filteredLearningsSearchResults = [];
  currentLearningSearchTerm = '';
  recentSearchItems = [];
  selectedViewDetailRow = null;

  constructor() {
    makeObservable(this, {
      userResults: observable,
      resourceResults: observable,
      locationResults: observable,
      totalResourceResultCount: observable,
      totalUserResultCount: observable,
      totalLocationResultCount: observable,
      awrSearchResults: observable,
      totalAwrSearchResultCount: observable,
      learningsSearchResults: observable,
      currentLearningSearchTerm: observable,
      recentSearchItems: observable,
      filteredLearningsSearchResults: observable,
      selectedViewDetailRow: observable,
      isSearching: observable,
      awrCancelTokenSource: observable,
      userCancelTokenSource: observable,
      locationCancelTokenSource: observable,
      searchUsers: action,
      searchLocations: action,
      searchResources: action,
      clearUserResults: action,
      searchAwrData: action,
      clearAwrSearchResults: action,
      searchLearnings: action,
      clearLearningsSearchResutls: action,
      getRecentSearchItems: action,
      clearSearchLearning: action,
    });
  }

  clearSearchLearning() {
    this.filteredLearningsSearchResults = [];
    this.learningsSearchResults = [];
    this.recentSearchItems = [];
  }
  async searchUsers(userSearch) {
    // Cancellation pattern documented for rapid typing prevention in debounced searches.
    // Replicate from searchAwrData for searchUsers:
    // - Cancel existing userCancelTokenSource before starting new request
    // - Create and store new CancelToken.source()
    // - Pass cancelToken as 5th argument to Api.search(userSearch, 'user', null, 20, cancelToken)
    // - Handle axios.isCancel(e) silently in catch block (set isSearching=false and return)
    // - Use try/catch/finally to manage isSearching consistently with AWR pattern
    // - isSearching observable allows components like UserSearch to react without local state
    if (this.userCancelTokenSource) {
      this.userCancelTokenSource.cancel();
      this.isSearching = false;
    }
    this.userCancelTokenSource = CancelToken.source();
    const cancelToken = this.userCancelTokenSource.token;

    try {
      this.isSearching = true;
      const response = await Api.search(
        userSearch,
        'user',
        null,
        20,
        cancelToken
      );
      const totalResultCount = response.headers['x-total-result-count'];

      if (response.data !== undefined) {
        if (response.data.results) {
          this.userResults = response.data.results;
        } else {
          this.userResults = response.data;
        }
        this.totalUserResultCount = totalResultCount;
      }
    } catch (e) {
      if (axios.isCancel(e)) {
        this.isSearching = false;
        return;
      }
      console.log(e);
    } finally {
      this.isSearching = false;
    }
  }

  async searchLocations(locationSearch) {
    // Cancellation pattern documented for rapid typing prevention in debounced searches.
    // Replicate from searchAwrData for searchLocations:
    // - Cancel existing locationCancelTokenSource before starting new request
    // - Create and store new CancelToken.source()
    // - Pass token to Api.search(locationSearch, 'location', {}, 200, cancelToken) which forwards to axios
    // - Handle axios.isCancel(e) silently in catch; isSearching managed before/in finally
    // - isSearching bubbled via MobX observable for components (e.g. LocationSearch) to consume
    if (this.locationCancelTokenSource) {
      this.locationCancelTokenSource.cancel();
      this.isSearching = false;
    }
    this.locationCancelTokenSource = CancelToken.source();
    const cancelToken = this.locationCancelTokenSource.token;

    try {
      this.isSearching = true;
      const response = await Api.search(
        locationSearch,
        'location',
        {},
        200,
        cancelToken
      );
      const totalResultCount = response.headers['x-total-result-count'];

      if (response.data !== undefined) {
        if (response.data.results) {
          this.locationResults = response.data.results;
        } else {
          this.locationResults = response.data;
        }
        this.totalLocationResultCount = totalResultCount;
      }
    } catch (e) {
      if (axios.isCancel(e)) {
        this.isSearching = false;
        return;
      }
      console.log(e);
    } finally {
      this.isSearching = false;
    }
  }

  async searchResources(resourceSearch) {
    try {
      this.isSearching = true;
      const response = await Api.search(resourceSearch, 'resource');
      const totalResultCount = response.headers['x-total-result-count'];

      if (response.data !== undefined) {
        if (response.data.results) {
          this.resourceResults = response.data.results.map((r) => {
            if (r.type && r.type === 'DLP Course Bundle') {
              r.type = 'Course Bundle';
            }
            return r;
          });
        } else {
          this.resourceResults = (response.data || []).map((r) => {
            if (r.type && r.type === 'DLP Course Bundle') {
              r.type = 'Course Bundle';
            }
            return r;
          });
        }
        this.totalResourceResultCount = totalResultCount;
      }
      this.isSearching = false;
    } catch (e) {
      this.isSearching = false;
      console.log(e);
    }
  }

  async searchAwrData(query) {
    // Cancellation pattern documented for rapid typing prevention in debounced searches.
    // Replicate to other search methods (searchUsers, etc.):
    // - Cancel existing token source before starting new request
    // - Store new CancelToken.source() for .cancel() on next search
    // - Pass token to Api.search(query, 'awr', {}, 200, cancelToken) which forwards to axios
    // - Handle axios.isCancel silently in catch; isSearching managed before/ in finally
    // - isSearching bubbled via MobX observable for components (e.g. AwrLearnerSearch) to consume
    if (this.awrCancelTokenSource) {
      this.awrCancelTokenSource.cancel();
      this.isSearching = false;
    }
    this.awrCancelTokenSource = CancelToken.source();
    const cancelToken = this.awrCancelTokenSource.token;

    try {
      this.isSearching = true;
      const response = await Api.search(query, 'awr', {}, 200, cancelToken);
      const totalAwrSearchResultCount =
        response.headers['x-total-result-count'];

      if (!_.isUndefined(response.data)) {
        if (response.data.results) {
          this.awrSearchResults = response.data.results;
        } else {
          this.awrSearchResults = response.data;
        }
        this.totalAwrSearchResultCount = totalAwrSearchResultCount;
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        this.isSearching = false;
        return;
      }
      console.log('Error in finding learners for AWR bulk edit', error);
    } finally {
      this.isSearching = false;
    }
  }

  async searchLearnings(query, filters) {
    try {
      this.isSearching = true;
      const response = await Api.search(query, 'learnings', filters);

      if (!_.isUndefined(response.data)) {
        this.isSearching = false;
      }

      let { results, recentSearchItems } = response.data || [];

      if (!_.isEmpty(results)) {
        results = (results || []).map((row) => ({
          ...row,
          isInPlan: CourseStore.isInPlan(row),
          isRequiredCourse:
            (CourseStore.requiredCourses || []).find((rc) => {
              return (
                parseInt(rc.id) === row.id ||
                (!_.isEmpty(rc.sourceId) && rc.sourceId === row.sourceId)
              );
            }) != null,
        }));
        this.learningsSearchResults = results;
        this.filteredLearningsSearchResults = results;
        this.totalResultCount = response.headers['x-total-result-count'];
      } else {
        this.learningsSearchResults = [];
        this.filteredLearningsSearchResults = [];
      }

      if (recentSearchItems) {
        this.recentSearchItems = (recentSearchItems || '').split(',');
      }
    } catch (e) {
      console.log('Error occurred while searching for learnings', e);
    } finally {
      this.isSearching = false;
    }
  }

  async getRecentSearchItems() {
    try {
      const { data } = await Api.getRecentSearchItems();
      this.recentSearchItems = data.recentSearchItems;
    } catch (e) {
      console.log(e);
    }
  }

  clearUserResults() {
    this.userResults = [];
  }

  clearAwrSearchResults() {
    this.awrSearchResults = [];
  }

  clearLearningsSearchResutls() {
    this.learningsSearchResults = [];
  }
}

export default new SearchStore();
